import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, Plus, Trash2, ExternalLink, Save, MessageSquare, RefreshCw, AlertCircle, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { formatDate } from "@/lib/dates";
import { DateInputIT } from "@/components/ui/date-input-it";
import { errMsg } from "@/lib/errors";

const EMPTY = { author_name: "", author_origin: "", rating: 5, text: "", language: "it", source: "google", url: "", published_at: "" };
const SOURCE_LABEL = { google: "Google", booking: "Booking.com", airbnb: "Airbnb", tripadvisor: "Tripadvisor", manual: "Diretta" };
const SOURCE_COLOR = {
  google:      "bg-amber-100 text-amber-900 border-amber-400",
  booking:     "bg-blue-100 text-blue-900 border-blue-400",
  airbnb:      "bg-rose-100 text-rose-900 border-rose-400",
  tripadvisor: "bg-emerald-100 text-emerald-900 border-emerald-400",
  manual:      "bg-slate-100 text-slate-700 border-slate-300",
};

export default function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [filterSource, setFilterSource] = useState("all");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/reviews");
      setReviews(r.data.reviews || []);
      localStorage.setItem("admin_reviews_last_viewed", new Date().toISOString());
    } catch {
      toast.error("Errore caricamento recensioni");
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshGoogleStatus = useCallback(async () => {
    try {
      const r = await api.get("/admin/reviews/google/status");
      setGoogleStatus(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { refresh(); refreshGoogleStatus(); }, [refresh, refreshGoogleStatus]);

  const syncGoogle = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/reviews/google/sync", { min_rating: 4 });
      const d = r.data || {};
      toast.success(`Sync Google OK — ${d.created || 0} nuove · ${d.updated || 0} aggiornate · media ${d.rating_avg ?? "—"}★`);
      await Promise.all([refresh(), refreshGoogleStatus()]);
    } catch (e) {
      toast.error(errMsg(e, "Errore sync Google"));
    } finally {
      setSyncing(false);
    }
  };

  const save = async () => {
    if (!editing.author_name.trim()) { toast.error("Nome autore obbligatorio"); return; }
    setSubmitting(true);
    try {
      await api.post("/admin/reviews", {
        ...editing,
        rating: Number(editing.rating),
        published_at: editing.published_at || null,
      });
      toast.success("Recensione aggiunta");
      setOpen(false);
      setEditing(EMPTY);
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    } finally {
      setSubmitting(false);
    }
  };

  const togglePublished = async (rev) => {
    try {
      await api.patch(`/admin/reviews/${rev.id}`, { is_published: !rev.is_published });
      toast.success(rev.is_published ? "Nascosta dal sito" : "Pubblicata sul sito");
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa recensione?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Eliminata");
      refresh();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const filtered = filterSource === "all" ? reviews : reviews.filter((r) => r.source === filterSource);
  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(2)
    : "—";
  const counts = {
    google: reviews.filter((r) => r.source === "google").length,
    booking: reviews.filter((r) => r.source === "booking").length,
    airbnb: reviews.filter((r) => r.source === "airbnb").length,
    tripadvisor: reviews.filter((r) => r.source === "tripadvisor").length,
    manual: reviews.filter((r) => r.source === "manual").length,
  };

  return (
    <div className="space-y-4" data-testid="reviews-tab">
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500 fill-amber-400" /> Recensioni
          </h3>
          <p className="text-xs text-[#635F59] mt-1 max-w-2xl">
            Sul tuo sito vengono mostrate solo le recensioni <strong>≥ 4★</strong>.
            Google si sincronizza automaticamente ogni 6h; Booking/Airbnb/Tripadvisor vanno copia-incollate dai relativi pannelli (le API ufficiali non lo permettono).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="font-display text-2xl text-[#AC5D45]">{avgRating} ★</div>
            <div className="text-[10px] uppercase text-[#635F59]">{reviews.length} totali</div>
          </div>
          <Button onClick={() => { setEditing(EMPTY); setOpen(true); }} data-testid="rev-add" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi
          </Button>
        </div>
      </div>

      {/* Google sync widget */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-4" data-testid="google-sync-widget">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${googleStatus?.configured ? "bg-emerald-100" : "bg-amber-100"}`}>
              {googleStatus?.configured
                ? <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                : <AlertCircle className="w-5 h-5 text-amber-700" />}
            </div>
            <div>
              <div className="font-semibold text-[#1E3A5F] text-sm">
                Sync recensioni Google
                {googleStatus?.configured ? <span className="ml-2 text-xs text-emerald-700">Attivo</span> : <span className="ml-2 text-xs text-amber-700">Da configurare</span>}
              </div>
              {googleStatus?.configured ? (
                <div className="text-xs text-[#635F59] mt-0.5">
                  Media Google: <strong className="text-[#AC5D45]">{googleStatus.rating_avg ?? "—"}★</strong>
                  {" · "}{googleStatus.rating_count ?? 0} recensioni totali
                  {googleStatus.last_sync && <> · Ultimo sync: {formatDate(googleStatus.last_sync)}</>}
                </div>
              ) : (
                <div className="text-xs text-[#635F59] mt-0.5">Inserisci <code>GOOGLE_PLACES_API_KEY</code> e <code>GOOGLE_PLACE_ID</code> in <code>/app/backend/.env</code> per attivare la sync automatica ogni 6h</div>
              )}
            </div>
          </div>
          <Button onClick={syncGoogle} disabled={syncing || !googleStatus?.configured} data-testid="google-sync-btn" className="bg-[#1E3A5F] hover:bg-[#152A45] text-white">
            <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Sync in corso…" : "Sync ora"}
          </Button>
        </div>
      </div>

      {/* Source filter chips */}
      <div className="flex flex-wrap gap-2 text-xs">
        <button onClick={() => setFilterSource("all")} className={`px-2.5 py-1 rounded-full border ${filterSource === "all" ? "bg-[#1E3A5F] text-white border-[#1E3A5F]" : "bg-white border-[#E8E2D9] text-[#635F59] hover:bg-[#FDFBF7]"}`}>Tutte ({reviews.length})</button>
        {Object.entries(SOURCE_LABEL).map(([k, label]) => (
          <button key={k} onClick={() => setFilterSource(k)} className={`px-2.5 py-1 rounded-full border ${filterSource === k ? "bg-[#AC5D45] text-white border-[#AC5D45]" : `${SOURCE_COLOR[k]} hover:opacity-80`}`}>
            {label} ({counts[k] || 0})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-white border border-[#E8E2D9] rounded-xl p-8 text-center text-[#635F59]">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#E8E2D9] rounded-xl p-10 text-center text-[#635F59]" data-testid="rev-empty">
          Nessuna recensione in questa categoria.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="rev-list">
          {filtered.map((r) => (
            <div key={r.id} className={`bg-white border border-[#E8E2D9] rounded-xl p-4 transition ${r.is_published === false ? "opacity-60" : ""}`} data-testid={`rev-${r.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="font-semibold text-[#1E3A5F]">{r.author_name}{r.author_origin && <span className="font-normal text-[#635F59] text-xs ml-1">· {r.author_origin}</span>}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge className={SOURCE_COLOR[r.source] || ""} variant="outline">{SOURCE_LABEL[r.source] || r.source}</Badge>
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "text-amber-500 fill-amber-400" : "text-[#E8E2D9]"}`} />
                      ))}
                    </div>
                    {r.is_featured && <Badge className="bg-pink-100 text-pink-800 border-pink-300 text-[9px]" variant="outline">⭐ In evidenza</Badge>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => togglePublished(r)} title={r.is_published === false ? "Pubblica sul sito" : "Nascondi dal sito"}>
                    {r.is_published === false ? <EyeOff className="w-4 h-4 text-[#635F59]" /> : <Eye className="w-4 h-4 text-emerald-700" />}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(r.id)} className="text-rose-600 hover:bg-rose-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {r.text && <p className="text-sm text-[#2D2926] whitespace-pre-wrap line-clamp-4">{r.text}</p>}
              <div className="text-[10px] text-[#635F59] mt-2 flex gap-3 items-center">
                {r.published_at && <span>{formatDate(r.published_at)}</span>}
                {r.url && (
                  <a href={r.url} target="_blank" rel="noreferrer" className="text-[#AC5D45] hover:underline inline-flex items-center gap-1">
                    Apri originale <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="rev-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Nuova recensione</DialogTitle></DialogHeader>
          <div className="grid gap-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Autore *</Label>
                <Input value={editing.author_name} onChange={(e) => setEditing({ ...editing, author_name: e.target.value })} placeholder="Es. Maria Bianchi" data-testid="rev-author" />
              </div>
              <div>
                <Label>Provenienza</Label>
                <Input value={editing.author_origin} onChange={(e) => setEditing({ ...editing, author_origin: e.target.value })} placeholder="Es. Milano, IT" data-testid="rev-origin" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Fonte</Label>
                <Select value={editing.source} onValueChange={(v) => setEditing({ ...editing, source: v })}>
                  <SelectTrigger data-testid="rev-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="google">Google</SelectItem>
                    <SelectItem value="booking">Booking.com</SelectItem>
                    <SelectItem value="airbnb">Airbnb</SelectItem>
                    <SelectItem value="tripadvisor">Tripadvisor</SelectItem>
                    <SelectItem value="manual">Diretta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Stelle</Label>
                <Input type="number" min={1} max={5} value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: e.target.value })} data-testid="rev-rating" />
              </div>
              <div>
                <Label>Data</Label>
                <DateInputIT value={editing.published_at?.slice(0, 10) || ""} onChange={(e) => setEditing({ ...editing, published_at: e.target.value })} data-testid="rev-date" />
              </div>
            </div>
            <div>
              <Label>Testo</Label>
              <Textarea rows={3} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} placeholder="Copia-incolla il testo della recensione qui" data-testid="rev-text" />
            </div>
            <div>
              <Label>URL originale (opzionale)</Label>
              <Input value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} placeholder="https://booking.com/... oppure https://airbnb.com/..." data-testid="rev-url" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={submitting} data-testid="rev-save" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
              <Save className="w-4 h-4 mr-1" /> {submitting ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
